import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { GameState, OwnedStone } from '../domain/types';
import {
  checkDailyReset,
  createInitialState,
  loadState,
  saveState,
} from '../domain/storage';

interface GameStateContextValue {
  state: GameState;
  setState: (updater: (s: GameState) => GameState) => void;
  ownedStone: (stoneId: string) => OwnedStone | undefined;
  hasStone: (stoneId: string) => boolean;
}

const GameStateContext = createContext<GameStateContextValue | null>(null);

export function GameStateProvider({ children }: { children: ReactNode }) {
  const [state, setStateInternal] = useState<GameState>(() => {
    const loaded = loadState();
    const initial = loaded ?? createInitialState();
    return checkDailyReset(initial);
  });

  // state変更時にLocalStorageへ永続化
  useEffect(() => {
    saveState(state);
  }, [state]);

  // タブがフォーカスされた時にも日次リセット判定(別タブで日付が変わった等の対策)
  useEffect(() => {
    const onFocus = () => {
      setStateInternal((s) => checkDailyReset(s));
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const value = useMemo<GameStateContextValue>(
    () => ({
      state,
      setState: (updater) => setStateInternal((s) => updater(s)),
      ownedStone: (stoneId) => state.inventory.find((s) => s.stoneId === stoneId),
      hasStone: (stoneId) => state.inventory.some((s) => s.stoneId === stoneId),
    }),
    [state],
  );

  return <GameStateContext.Provider value={value}>{children}</GameStateContext.Provider>;
}

export function useGameState(): GameStateContextValue {
  const ctx = useContext(GameStateContext);
  if (!ctx) throw new Error('useGameState must be used within GameStateProvider');
  return ctx;
}
